import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
	GridItem,
	Editable,
	EditablePreview,
	EditableInput,
	Box,
	Image,
	createStandaloneToast,
	Badge,
	Avatar,
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
	Tooltip,
	layout,
	Button,
} from '@chakra-ui/react';
import { UilLinkAlt } from '@iconscout/react-unicons';
import { UilArrowToBottom } from '@iconscout/react-unicons';
import { UilEllipsisH } from '@iconscout/react-unicons';
import queries from '../../utils/query';
import { useMutation } from '@apollo/client';
import { squadBadgeColors } from '../../utils/layoutConfig';
import LayoutLastUpdate from './LayoutLastUpdate';
import SendToCompileBtn from '../header/SendToCompileBtn';

function getBadgeColor(type) {
	return squadBadgeColors[(type || '').toLowerCase()]
		? squadBadgeColors[(type || '').toLowerCase()]
		: squadBadgeColors.default;
}

function timestampToLocaleDate(timestamp) {
	return new Date(Number(timestamp)).toLocaleDateString('pt-br', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
}

function setProjectName(value, createOrUpdateProjectMutation, toast, layoutProps, setLayoutProps, setRenameDisable) {
	setRenameDisable(true);
	if (value == layoutProps.viewName) {
		return;
	}
	const newLayout = { ...layoutProps };
	newLayout.viewName = value;
	//limpando pois apollo é fortemente tipado
	Object.keys(newLayout).map((key) => {
		return newLayout[key] == null || newLayout[key] == undefined ? (newLayout[key] = '') : false;
	});
	createOrUpdateProjectMutation({ variables: layoutProps })
		.then(() => {
			toast(getToastMessage('renomeado', true, layoutProps));
			setLayoutProps(newLayout);
		})
		.catch(() => {
			toast(getToastMessage('renomeado', true, layoutProps));
		});
}
function allowRename(renameInput, setRenameDisable) {
	setRenameDisable(false);
	renameInput.firstChild.focus();
}
// function cloneProject(createOrUpdateProjectMutation,toast,layoutProps,viewList,index,setViewList){
//     let cloned = {...layoutProps};
//     cloned.viewName = 'Cópia de '+cloned.viewName
//     //limpando pois apollo é fortemente tipado
//     Object.keys(cloned).map(key=>{
//         return cloned[key]==null||cloned[key]==undefined?cloned[key]='':false
//     })
//     cloned.viewId = null

//     createOrUpdateProjectMutation({variables:cloned}).then((result)=>{
//         let viewId = result.data.createViewListItem.id;
//         cloned.viewId=viewId;
//         cloned.createdAt = new Date();
//         let newViewList = [...viewList];
//         newViewList.splice(index,0,cloned);
//         setViewList(newViewList)
//         toast(getToastMessage('clonado',true,layoutProps))
//     }).catch((e)=>{
//         toast(getToastMessage('clonado',false,layoutProps))
//     })
// }

function cloneProject({
	duplicateViewMutation,
	toast,
	viewList,
	setViewList,
	index,
	layoutProps,
	setCloningModalIsOpen,
}) {
	setCloningModalIsOpen(true);
	const { viewId } = layoutProps;
	duplicateViewMutation({ variables: { viewId } })
		.then(({ data }) => {
			const clonedData = data.duplicateView.clonedData ? JSON.parse(data.duplicateView.clonedData) : null;
			if (!clonedData) {
				setCloningModalIsOpen(false);
				toast(getToastMessage('clonado', false, layoutProps));
				return;
			}
			const newViewList = [...viewList];
			newViewList.push(clonedData);
			setViewList(newViewList);
			setCloningModalIsOpen(false);
			toast(getToastMessage('clonado', true, layoutProps));
		})
		.catch((e) => {
			setCloningModalIsOpen(false);
			toast(getToastMessage('clonado', false, layoutProps));
		});
}

function getToastMessage(type, success, layoutProps) {
	if (success) {
		return {
			title: `Projeto ${type} com sucesso!`,
			description: `Projeto '${layoutProps.viewName}' ${type} com sucesso!`,
			status: 'success',
			duration: 3000,
			isClosable: true,
			position: 'top',
		};
	} else {
		return {
			title: `Projeto não foi ${type}!`,
			description: `Houve um erro com a requisição, por favor, tente novamente`,
			status: 'error',
			duration: 3000,
			isClosable: true,
			position: 'top',
		};
	}
}

function openDeleteViewModal({ dMOnOpen, setViewToDelete, layoutProps }) {
	const { viewName, viewId } = layoutProps;
	setViewToDelete({ viewName, viewId });
	dMOnOpen();
}

const LayoutMiniatureGrid = ({
	layout,
	viewList,
	dMOnOpen,
	setViewList,
	index,
	gridStyle,
	setViewToDelete,
	setCloningModalIsOpen,
}) => {
	const navigate = useNavigate();
	const [createOrUpdateProjectMutation, { data, apolloLoading, error }] = useMutation(
		queries.CREATE_OR_UPDATE_VIEW_LIST_ITEM,
	);
	const [duplicateViewMutation, { dupData }] = useMutation(queries.DUPLICATE_VIEW);
	const toast = createStandaloneToast();
	const [renameDisable, setRenameDisable] = React.useState(true);
	const [layoutProps, setLayoutProps] = React.useState(layout);
	let renameInput;
	const isGrid = gridStyle == 'grid' ? true : false;

	function handleClick() {
		navigate('experimental/' + layoutProps.viewId);
	}

	return (
		<GridItem
			role="group"
			flex={'1 1 23%'}
			onDoubleClick={handleClick}
			_hover={{ boxShadow: '0 0 2px 1pt #ECC94B' }}
			borderStyle={'solid'}
			transition={'0.1s'}
			cursor="pointer"
			display={isGrid ? 'grid' : 'flex'}
			minW={isGrid ? '271px' : 'unset'}
			width={isGrid ? 'auto' : '100%'}
			height={isGrid ? '290px' : '74px'}
			borderWidth="1px"
			borderRadius="lg"
			overflow={isGrid ? 'hidden' : 'visible'}
		>
			<Box
				maxW={isGrid ? 'auto' : 'unset'}
				minW={isGrid ? 'auto' : 'unset'}
				d="flex"
				w="100%"
				justifyContent="space-between;"
				flexDirection={isGrid ? 'column' : 'row'}
				overflow={isGrid ? 'hidden' : 'visible'}
			>
				<Box position="relative" flexBasis={isGrid ? '60%' : '8%'} userSelect={'none'}>
					<Image
						zIndex={-1}
						position={isGrid ? 'absolute' : 'relative'}
						height={isGrid ? 'auto' : '70px'}
						maxW={'auto'}
						left={isGrid ? 0 : 'unset'}
						right={isGrid ? 0 : 'unset'}
						sx={{ margin: ' 0 auto;' }}
						src={
							layoutProps.viewPreview
								? layoutProps.viewPreview
								: 'https://via.placeholder.com/250x400/bbbbbb/bbbbbb?text=MZ'
						}
						alt={'Miniatura ' + layoutProps.viewName}
					/>
				</Box>
				<Box
					alignItems={isGrid ? 'auto' : 'center'}
					backgroundColor="white"
					display={isGrid ? 'block' : 'flex'}
					flexDir={'row'}
					flexBasis={isGrid ? 'auto' : '92%'}
					borderTop={isGrid ? '1px solid #CBD5E0;' : 0}
					px={4}
					pt={3}
					pb={2}
				>
					<Box
						flexBasis={'28%'}
						mb={1}
						fontWeight="bold"
						lineHeight="tight"
						fontSize="xs"
						isTruncated
						overflow={renameDisable ? 'hidden' : 'visible'}
					>
						<Editable
							ref={(input) => {
								renameInput = input;
							}}
							isDisabled={renameDisable}
							startWithEditView={true}
							onSubmit={(e) =>
								setProjectName(e, createOrUpdateProjectMutation, toast, layoutProps, setLayoutProps, setRenameDisable)
							}
							defaultValue={layoutProps.viewName}
						>
							<EditablePreview />
							<EditableInput autoFocus={!renameDisable} />
						</Editable>
					</Box>
					<Box
						flexBasis={'33%'}
						d="flex"
						justifyContent={isGrid ? 'space-between;' : 'space-around'}
						alignItems={'baseline'}
					>
						<Box as="span" color="gray.600" fontSize="xs">
							{'Criado em: ' + timestampToLocaleDate(layoutProps.createdAt)}
						</Box>
						<Badge borderRadius="4px" px="2" colorScheme={getBadgeColor(layoutProps.type)}>
							{layoutProps.type ? layoutProps.type : 'projeto'}
						</Badge>
					</Box>
					<LayoutLastUpdate {...{ isGrid, Tooltip, viewId: layoutProps.viewId }} />
					<Box
						flexBasis={'21%'}
						mt={isGrid ? 3 : 0}
						_groupHover={{ height: '32px', visibility: 'visible', opacity: '1' }}
						opacity="0"
						transition={'0.2s'}
						visibility="hidden"
						height={0}
					>
						<Box d="flex" justifyContent="center" sx={{ gap: '50px' }}>
							<Tooltip label={'Link do projeto'}>
								<Box _hover={{ svg: { fill: 'yellow.400' } }}>
									<UilLinkAlt color="gray.700" />
								</Box>
							</Tooltip>
							<Tooltip label={'Download do Projeto'}>
								{/* <SendToCompileBtn viewId={viewId}> */}
								<Box _hover={{ svg: { fill: 'yellow.400' } }}>
									{/* <Button variant={'unstyled'}> */}
									<UilArrowToBottom color="gray.700" />
									{/* </Button>                                */}
								</Box>
								{/* </SendToCompileBtn> */}
							</Tooltip>
							<Box _hover={{ svg: { fill: 'yellow.400' } }}>
								<Menu>
									<Tooltip label={'Mais Opções'}>
										<MenuButton>
											<UilEllipsisH color="gray.700" />
										</MenuButton>
									</Tooltip>
									<MenuList minW={'185px'} fontSize={'xs'}>
										<MenuItem
											onClick={(e) => {
												cloneProject({
													duplicateViewMutation,
													toast,
													viewList,
													setViewList,
													index,
													...dupData,
													layoutProps,
													setCloningModalIsOpen,
												});
											}}
										>
											Duplicar
										</MenuItem>
										<MenuItem>
											<Box as="a" target="_blank" href={'builder/' + layoutProps.viewId}>
												Abrir em nova aba
											</Box>
										</MenuItem>
										<MenuItem
											onClick={(e) => {
												allowRename(renameInput, setRenameDisable);
											}}
										>
											Renomear
										</MenuItem>
										<MenuItem
											color={'red.500'}
											onClick={(e) => {
												openDeleteViewModal({ dMOnOpen, setViewToDelete, layoutProps });
											}}
										>
											Deletar
										</MenuItem>
									</MenuList>
								</Menu>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</GridItem>
		// <div className='layout-miniature' onClick={(e) => handleClick()}>
		//     <figure>
		//         <img alt={props.viewName} src={props.viewPreview?props.viewPreview:'https://via.placeholder.com/300x500'} />
		//         <figcaption>{props.viewName}</figcaption>
		//     </figure>
		// </div>
	);
};

export default LayoutMiniatureGrid;
